import { useState } from "react";
import { X, Clock, Zap, ShoppingCart } from "lucide-react";

interface WhatsAppWidgetProps {
  phoneNumber?: string;
  agentName?: string;
  agentImage?: string;
}

const WhatsAppWidget = ({
  phoneNumber = "",
  agentName = "مساعد دايتتك",
  agentImage,
}: WhatsAppWidgetProps) => {
  const [isOpen, setIsOpen] = useState(() => window.innerWidth >= 768);

  const handleWhatsAppClick = () => {
    if (!phoneNumber) return;
    const url = `https://wa.me/${phoneNumber.replace(/\D/g, "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col items-start gap-3" dir="rtl">
      {isOpen && (
        <div className="bg-card border border-border rounded-2xl shadow-2xl w-[calc(100vw-3rem)] sm:w-[300px] overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="bg-primary px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                {agentImage ? (
                  <img
                    src={agentImage}
                    alt={agentName}
                    className="w-10 h-10 rounded-full object-cover border-2 border-primary-foreground/20"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary-foreground/15 border-2 border-primary-foreground/20 flex items-center justify-center">
                    <span className="text-primary-foreground font-bold text-lg">
                      {agentName.charAt(0)}
                    </span>
                  </div>
                )}
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-primary" />
              </div>
              <div>
                <p className="text-primary-foreground font-bold text-sm leading-none mb-1">
                  {agentName}
                </p>
                <p className="text-green-300 text-xs">● متاح على مدار الساعة</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-primary-foreground/60 hover:text-primary-foreground transition-colors p-1 rounded-full hover:bg-primary-foreground/10"
            >
              <X size={16} />
            </button>
          </div>

          {/* Message bubble */}
          <div className="px-4 pt-4 pb-3 bg-muted">
            <div className="bg-card rounded-xl rounded-tr-none px-4 py-3 shadow-sm border border-border/50">
              <p className="text-foreground text-sm leading-relaxed">
                هلا! 👋 أنا هنا لمساعدتك تختار الباقة المناسبة من أدوات دايتتك
              </p>
              <p className="text-muted-foreground text-[10px] mt-1.5 text-left">الآن</p>
            </div>
          </div>

          {/* Features */}
          <div className="px-4 pb-4 bg-muted space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Clock size={13} className="text-primary shrink-0" />
              <span>متاح ٢٤ ساعة، ٧ أيام — بدون انتظار</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Zap size={13} className="text-primary shrink-0" />
              <span>ردود فورية على جميع استفساراتك</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <ShoppingCart size={13} className="text-primary shrink-0" />
              <span>مساعدة كاملة في عملية الاشتراك</span>
            </div>
          </div>

          {/* CTA */}
          <div className="px-4 pt-3 pb-2 bg-card border-t border-border">
            <button
              onClick={handleWhatsAppClick}
              disabled={!phoneNumber}
              className="w-full bg-[#25D366] hover:bg-[#20c05a] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <WhatsAppIcon className="w-5 h-5 fill-current" />
              تحدث معي على الواتساب
            </button>
            <p className="text-center text-muted-foreground text-[10px] mt-2">بواسطة دايتتك</p>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-14 h-14 bg-[#25D366] hover:bg-[#20c05a] rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
        aria-label="تواصل معنا عبر واتساب"
      >
        {isOpen ? (
          <X className="text-white" size={24} />
        ) : (
          <WhatsAppIcon className="w-7 h-7 fill-white" />
        )}
      </button>
    </div>
  );
};

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default WhatsAppWidget;
